import {
  IconBank,
  IconCar,
  IconCard,
  IconFile,
  IconHouseWork,
  IconLightbulb,
  IconMobilePhone,
  IconTrend,
  IconTrendNegative,
} from '@capitalise/ui-design-system';

export const MonitorDelay = 9000;
export const BUSINESS_FORCE_REFRESH_QUERY = 'forceRefresh';

export const CompanyType = Object.freeze({
  soleTrader: 'SOLE_TRADER',
  limitedLiabilityPartnership: 'LIMITED_LIABILITY_PARTNERSHIP',
  limitedCompany: 'LIMITED_COMPANY',
  startup: 'STARTUP',
});

export const SectorType = Object.freeze({
  agriculture: '1',
  automotive: '2',
  businesServices: '3',
  charities: '29',
  construction: '4',
  courier: '5',
  distribution: '6',
  education: '27',
  financialServices: '7',
  haulage: '8',
  healthcare: '9',
  hotels: '10',
  importExport: '11',
  itRelated: '12',
  manufacturing: '13',
  mediaEntertainment: '31',
  printPublishing: '14',
  professionalServices: '15',
  property: '16',
  recruitment: '17',
  retail: '18',
  securityServices: '19',
  trade: '20',
  transportation: '21',
  wholesale: '22',
  other: '23',
  none: '24',
});
export const FacilitySource = Object.freeze({
  publicAccountingProvider: 'PUBLIC_ACCOUNTING_PROVIDER',
});

export const BusinessCreditProfileSections = Object.freeze({
  summary: 'summary',
  creditScore: 'credit-score',
  creditSearches: 'credit-searches',
  creditRiskFactor: 'credit-risk-factor',
  publicRecord: 'publicRecord',
  paymentPerformance: 'payment-performance',
  cashAndCapital: 'cash-and-capital',
  legalNotices: 'legal-notices',
  annualAccounts: 'annual-accounts',
});


export const SectorTypeLabel = Object.freeze({
  [SectorType.agriculture]: 'Agriculture',
  [SectorType.automotive]: 'Automotive',
  [SectorType.businesServices]: 'Business Services',
  [SectorType.charities]: 'Charities',
  [SectorType.construction]: 'Construction',
  [SectorType.courier]: 'Courier',
  [SectorType.distribution]: 'Distribution',
  [SectorType.education]: 'Education',
  [SectorType.financialServices]: 'Financial Services',
  [SectorType.haulage]: 'Haulage',
  [SectorType.healthcare]: 'Healthcare/Care',
  [SectorType.hotels]: 'Hotels/Guest House/Public House',
  [SectorType.importExport]: 'Import/Export',
  [SectorType.itRelated]: 'IT Related',
  [SectorType.manufacturing]: 'Manufacturing',
  [SectorType.mediaEntertainment]: 'Media & Entertainment',
  [SectorType.printPublishing]: 'Print/Publishing',
  [SectorType.professionalServices]: 'Professional Services',
  [SectorType.property]: 'Property',
  [SectorType.recruitment]: 'Recruitment',
  [SectorType.retail]: 'Retail',
  [SectorType.securityServices]: 'Security Services',
  [SectorType.trade]: 'Trade',
  [SectorType.transportation]: 'Transportation',
  [SectorType.wholesale]: 'Wholesale',
  [SectorType.other]: 'Other',
  [SectorType.none]: 'None',
});

export const InsightsCategory = Object.freeze({
  creditScore: 'creditScore',
  paymentPerformance: 'paymentPerformance',
  creditProfile: 'creditProfile',
  boostingCashflow: 'boostingCashflow',
  legalNotices: 'legalNotices',
});

export const BusinessCreditProfileSectionNames = Object.freeze({
  [BusinessCreditProfileSections.summary]: 'Credit profile',
  [BusinessCreditProfileSections.creditScore]: 'Credit score',
  [BusinessCreditProfileSections.creditSearches]: 'Credit searches',
  [BusinessCreditProfileSections.creditRiskFactor]: 'Credit risk factors',
  [BusinessCreditProfileSections.cashAndCapital]: 'Cash and capital',
  [BusinessCreditProfileSections.publicRecord]: 'Public record',
  [BusinessCreditProfileSections.paymentPerformance]: 'Payment performance',
  [BusinessCreditProfileSections.legalNotices]: 'Legal notices',
  [BusinessCreditProfileSections.annualAccounts]: 'Annual Accounts',
});

export const BusinessCreditProfileSectionNamesByCategory = Object.freeze({
  [InsightsCategory.summary]: 'Credit profile',
  [InsightsCategory.creditScore]: 'Credit score',
  [BusinessCreditProfileSections.creditSearches]: 'Credit searches',
  [InsightsCategory.creditRiskFactor]: 'Credit risk factors',
  [InsightsCategory.cashAndCapital]: 'Cash and capital',
  [InsightsCategory.paymentPerformance]: 'Payment performance',
  [InsightsCategory.legalNotices]: 'Legal notices',
});

export const CreditScoreType = Object.freeze({
  creditScoreNone: '0',
  creditScoreA: '1',
  creditScoreB: '2',
  creditScoreC: '3',
  creditScoreD: '4',
  creditScoreE: '5',
  creditScoreF: '6',
  creditScoreG: '7',
  creditScoreH: '8',
});

export const CreditScoreLetter = Object.freeze({
  A: CreditScoreType.creditScoreA,
  B: CreditScoreType.creditScoreB,
  C: CreditScoreType.creditScoreC,
  D: CreditScoreType.creditScoreD,
  E: CreditScoreType.creditScoreE,
  F: CreditScoreType.creditScoreF,
  G: CreditScoreType.creditScoreG,
  H: CreditScoreType.creditScoreH,
});

export const CreditScoreBaseColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green',
  [CreditScoreType.creditScoreB]: 'green',
  [CreditScoreType.creditScoreC]: 'green',
  [CreditScoreType.creditScoreD]: 'warning',
  [CreditScoreType.creditScoreE]: 'warning',
  [CreditScoreType.creditScoreF]: 'danger',
  [CreditScoreType.creditScoreG]: 'danger',
  [CreditScoreType.creditScoreH]: 'danger',
});

export const CreditScoreRisk = Object.freeze({
  [CreditScoreType.creditScoreA]: 'minimum risk',
  [CreditScoreType.creditScoreB]: 'low risk',
  [CreditScoreType.creditScoreC]: 'below average risk',
  [CreditScoreType.creditScoreD]: 'above average risk',
  [CreditScoreType.creditScoreE]: 'high risk',
  [CreditScoreType.creditScoreF]: 'maximum risk',
  [CreditScoreType.creditScoreG]: 'maximum risk',
  [CreditScoreType.creditScoreH]: 'maximum risk',
});

export const CreditScoreRiskFree = Object.freeze({
  [CreditScoreType.creditScoreA]: 'Good',
  [CreditScoreType.creditScoreB]: 'Good',
  [CreditScoreType.creditScoreC]: 'Average',
  [CreditScoreType.creditScoreD]: 'Average',
  [CreditScoreType.creditScoreE]: 'Poor',
  [CreditScoreType.creditScoreF]: 'Poor',
  [CreditScoreType.creditScoreG]: 'Poor',
  [CreditScoreType.creditScoreH]: 'Poor',
});

export const CreditRiskFactorType = Object.freeze({
  positive: 1,
  negative: -1,
});

export const CreditRiskFactorLabel = Object.freeze({
  [CreditRiskFactorType.positive]: 'Positives',
  [CreditRiskFactorType.negative]: 'Negatives',
});

export const CreditRiskFactorBaseColor = Object.freeze({
  [CreditRiskFactorType.positive]: 'green',
  [CreditRiskFactorType.negative]: 'warning',
});

export const PaymentPerformancePaymentPattern = Object.freeze({
  CONSISTENT: 'CONSISTENT',
  WORSENING: 'WORSENING',
  NOTICEABLY_WORSENING: 'NOTICEABLY_WORSENING',
  SIGNIFICANTLY_WORSENING: 'SIGNIFICANTLY_WORSENING',
  IMPROVING: 'IMPROVING',
  NOTICEABLY_IMPROVING: 'NOTICEABLY_IMPROVING',
  SIGNIFICANTLY_IMPROVING: 'SIGNIFICANTLY_IMPROVING',
});

export const SentimentStatus = Object.freeze({
  positive: 'positive',
  negative: 'negative',
  neutral: 'neutral',
});

export const SentimentStatusColor = Object.freeze({
  [SentimentStatus.positive]: 'green',
  [SentimentStatus.negative]: 'danger',
  [SentimentStatus.neutral]: 'grey',
});

export const PaymentPerformancePaymentPatternSentiment = Object.freeze({
  [PaymentPerformancePaymentPattern.CONSISTENT]: SentimentStatus.neutral,
  [PaymentPerformancePaymentPattern.WORSENING]: SentimentStatus.negative,
  [PaymentPerformancePaymentPattern.NOTICEABLY_WORSENING]: SentimentStatus.negative,
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_WORSENING]: SentimentStatus.negative,
  [PaymentPerformancePaymentPattern.IMPROVING]: SentimentStatus.positive,
  [PaymentPerformancePaymentPattern.NOTICEABLY_IMPROVING]: SentimentStatus.positive,
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_IMPROVING]: SentimentStatus.positive,
});

export const PaymentPerformancePaymentPatternBaseColor = Object.freeze({
  [PaymentPerformancePaymentPattern.CONSISTENT]: 'grey',
  [PaymentPerformancePaymentPattern.WORSENING]: 'warning',
  [PaymentPerformancePaymentPattern.NOTICEABLY_WORSENING]: 'warning',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_WORSENING]: 'warning',
  [PaymentPerformancePaymentPattern.IMPROVING]: 'green',
  [PaymentPerformancePaymentPattern.NOTICEABLY_IMPROVING]: 'green',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_IMPROVING]: 'green',
});

export const PaymentPerformancePaymentPatternIcon = Object.freeze({
  [PaymentPerformancePaymentPattern.CONSISTENT]: null,
  [PaymentPerformancePaymentPattern.WORSENING]: IconTrendNegative,
  [PaymentPerformancePaymentPattern.NOTICEABLY_WORSENING]: IconTrendNegative,
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_WORSENING]: IconTrendNegative,
  [PaymentPerformancePaymentPattern.IMPROVING]: IconTrend,
  [PaymentPerformancePaymentPattern.NOTICEABLY_IMPROVING]: IconTrend,
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_IMPROVING]: IconTrend,
});

export const PaymentPerformancePaymentPatternLabel = Object.freeze({
  [PaymentPerformancePaymentPattern.CONSISTENT]: 'Consistent',
  [PaymentPerformancePaymentPattern.WORSENING]: 'Worsening',
  [PaymentPerformancePaymentPattern.NOTICEABLY_WORSENING]: 'Worsening',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_WORSENING]: 'Worsening',
  [PaymentPerformancePaymentPattern.IMPROVING]: 'Improving',
  [PaymentPerformancePaymentPattern.NOTICEABLY_IMPROVING]: 'Improving',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_IMPROVING]: 'Improving',
});

export const LegalNoticeType = Object.freeze({
  registered: 1,
  nonRegistered: 0,
});

export const LegalNoticeTypeBaseColor = Object.freeze({
  [LegalNoticeType.registered]: 'danger',
  [LegalNoticeType.nonRegistered]: 'green',
});

export const BusinessCreditProfileSectionDescriptions = Object.freeze({
  [BusinessCreditProfileSections.summary]:
    'Your credit profile is essential to the financial health of your business. It impacts how much credit suppliers will extend to you and the interest rates you’ll pay on loans.',
  [BusinessCreditProfileSections.creditScore]:
    'Your credit score is used to determine the financial health and risk of the business.',
  [BusinessCreditProfileSections.creditSearches]:
    'Searches made on your company credit profile by other companies, banks or lenders.',
  [BusinessCreditProfileSections.creditRiskFactor]:
    'The underlying criteria that credit bureaus use to calculate your score.',
  [BusinessCreditProfileSections.cashAndCapital]:
    'See a breakdown of the cash and capital in the business and how they impact your business credit score.',
  [BusinessCreditProfileSections.publicRecord]:
    'A summary of your company records which are registered publicly at Companies House.',
  [BusinessCreditProfileSections.paymentPerformance]:
    'An overview of how your company performs when making payments to supplier terms.',
  [BusinessCreditProfileSections.legalNotices]:
    'County Court Judgements (CCJs) and other negative events registered against your company.',
  [BusinessCreditProfileSections.annualAccounts]:
    'A breakdown of your latest publicly filed financial statements at Companies House.',
});

export const BusinessCreditProfileInsights = Object.freeze({
  [BusinessCreditProfileSections.creditScore]: [
    {
      title: `Your credit limit is recommended as {{currency}}{{creditLimit}}`,
      description: 'This is the maximum amount of credit recommended to lenders.',
    },
    {
      title: `{{creditEnquiriesLast12Months}} enquiries were made on your credit profile`,
      description: 'Searches made by third parties in the last 12 months.',
    },
    {
      title: `The average credit score in your industry is {{scoreAvgSector}}.`,
      description: 'How do you compare against the industry average?',
    },
  ],
  [BusinessCreditProfileSections.paymentPerformance]: [
    {
      title: `Your company is performing {{paymentPerformanceIndustryPattern}} the industry average.`,
      description: 'How your company compares to companies of a similar size.',
    },
    {
      title: `The payment average in your industry is {{paymentPerformanceIndustryAvgDBT3Mnths}} late.`,
      description: 'How your company compares to companies of a similar size.',
    },
  ],
});

export const PaymentPerformancePaymentTerm = Object.freeze({
  WITHIN_TERMS: 'WITHIN_TERMS',
  BEYOND_TERMS: 'BEYOND_TERMS',
});

export const PaymentPerformancePaymentTermLabel = Object.freeze({
  [PaymentPerformancePaymentTerm.WITHIN_TERMS]: 'Within terms',
  [PaymentPerformancePaymentTerm.BEYOND_TERMS]: 'Beyond terms',
});

export const PaymentPerformancePaymentPatternLabelSimplified = Object.freeze({
  [PaymentPerformancePaymentPattern.CONSISTENT]: 'Consistent',
  [PaymentPerformancePaymentPattern.WORSENING]: 'Worsened',
  [PaymentPerformancePaymentPattern.NOTICEABLY_WORSENING]: 'Worsened',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_WORSENING]: 'Worsened',
  [PaymentPerformancePaymentPattern.IMPROVING]: 'Improved',
  [PaymentPerformancePaymentPattern.NOTICEABLY_IMPROVING]: 'Improved',
  [PaymentPerformancePaymentPattern.SIGNIFICANTLY_IMPROVING]: 'Improved',
});

export const PAYMENT_PERFORMANCE_DESCRIPTION_INTRO = 'Your payments to suppliers are ';
export const PAYMENT_PERFORMANCE_DESCRIPTION_CONNECTION = ' and have ';
export const PAYMENT_PERFORMANCE_DESCRIPTION_CONCLUSION = ' over the last 3 months.';

export const PaymentPerformanceIndustryPaymentPattern = Object.freeze({
  CONSISTENT_WITH_INDUSTRY_AVG: 'CONSISTENT_WITH_INDUSTRY_AVG',
  SLOWER_THAN_INDUSTRY_AVG: 'SLOWER_THAN_INDUSTRY_AVG',
  FASTER_THAN_INDUSTRY_AVG: 'FASTER_THAN_INDUSTRY_AVG',
});

export const PaymentPerformanceIndustryPaymentPatternLabel = Object.freeze({
  [PaymentPerformanceIndustryPaymentPattern.CONSISTENT_WITH_INDUSTRY_AVG]: 'consistent with',
  [PaymentPerformanceIndustryPaymentPattern.SLOWER_THAN_INDUSTRY_AVG]: 'slower than',
  [PaymentPerformanceIndustryPaymentPattern.FASTER_THAN_INDUSTRY_AVG]: 'faster than',
});

export const CaseType = Object.freeze({
  funding: 'FUNDING',
  recoveryAndProtect: 'RECOVERY_AND_PROTECT', //R&D Tax Claim - Debt recovery
  raise: 'RAISE', // Innovation Grant
  creditImprovement: 'PROTECT', // Credit Improvement
  innovationGrant: 'GRANT', // Innovation Grant
});

export const CaseStateType = Object.freeze({
  action: 'ACTION',
  complete: 'COMPLETE',
  declined: 'DECLINED',
  pending: 'PENDING',
});

export const CaseStateTypeOrder = Object.freeze({
  [CaseStateType.action]: 2,
  [CaseStateType.complete]: 3,
  [CaseStateType.declined]: 0,
  [CaseStateType.pending]: 1,
});

export const CaseStateTypeColors = Object.freeze({
  [CaseStateType.action]: 'warning',
  [CaseStateType.complete]: 'green',
  [CaseStateType.declined]: 'danger',
  [CaseStateType.pending]: 'grey',
});

export const ProductType = Object.freeze({
  termLoan: 'TERM_LOAN',
  fixedLoanTerm: 'FIXED_LOAN_TERM',
  assetBackedFinance: 'ASSET_BACKED_FINANCE',
  creditReview: 'CREDIT_REVIEW',
  merchantCashAdvance: 'MERCHANT_CASH_ADVANCE',
  invoiceFinancing: 'INVOICE_FINANCE',
  ecommerceFunding: 'ECOMMERCE_FUNDING',
  tradeFinance: 'TRADE_FINANCE',
  commercialProperty: 'COMMERCIAL_PROPERTY',
  debtRecovery: 'DEBT_RECOVERY',
  researchAndDevelopmentTax: 'RESEARCH_AND_DEVELOPMENT_TAX',
  capitalAllowances: 'CAPITAL_ALLOWANCES',
  creditImprovement: 'CREDIT_IMPROVEMENT',
  grants: 'GRANTS',
  governmentBacked: 'GOVERNMENT_BACKED',
  recoveryLoanScheme: 'RECOVERY_LOAN_SCHEME',
});

export const ProductTypeLabel = Object.freeze({
  [ProductType.assetBackedFinance]: 'Asset Backed Finance',
  [ProductType.creditReview]: 'Credit Review',
  [ProductType.ecommerceFunding]: 'E-commerce Funding',
  [ProductType.fixedLoanTerm]: 'Fixed Loan Terms',
  [ProductType.invoiceFinancing]: 'Invoice Finance',
  [ProductType.merchantCashAdvance]: 'Merchant Cash Advance',
  [ProductType.termLoan]: 'Term Loan',
  [ProductType.tradeFinance]: 'Trade Finance',
  [ProductType.commercialProperty]: 'Property Finance',
  [ProductType.debtRecovery]: 'Debt Recovery',
  [ProductType.researchAndDevelopmentTax]: 'R&D Tax',
  [ProductType.capitalAllowances]: 'Capital Allowances',
  [ProductType.creditImprovement]: 'Credit Improvement',
  [ProductType.grants]: 'Grants',
  [ProductType.recoveryLoanScheme]: 'Recovery Loan Scheme',
});

export const ProductTypeNarrative = Object.freeze({
  [ProductType.assetBackedFinance]:
    'Cost effective funding to invest in or refinance business equipment by spreading the cost over an agreed period of time.',
  [ProductType.commercialProperty]:
    'Finance secured by way of 1st charges against properties, whether long or short term; residential, commercial or mixed-use; additional properties or portfolio refinances; refurbs or ground up developments',
  [ProductType.creditImprovement]:
    'A good credit score demonstrates a creditworthy business to lenders, which can lead to reduced rates, a higher borrowing allowance and flexible repayments.',
  [ProductType.creditReview]:
    'Review service from Experian to check the accuaracy of the company credit score.',
  [ProductType.invoiceFinancing]:
    'Popular option for businesses seeking to raise funding quickly and effectively by advancing a percentage of their receivables.',
  [ProductType.merchantCashAdvance]:
    'Raise funding against future credit card sales. A merchant cash advance offers short payment terms with small, regular payments.',
  [ProductType.termLoan]:
    'Multi-purpose loan designed for daily operational costs from staff wages to paying rent. An easy and hassle-free way to secure funding.',
  [ProductType.tradeFinance]:
    'Fund suppliers upfront based on confirmed orders and finished goods. A proven method to increase business and trade with confidence.',
});

export const FundingMatchStatus = Object.freeze({
  submitted: 0,
  inReview: 1,
  passed: 2,
  paidOut: 3,
  lenderDecline: 4,
  notAppropriate: 5,
  incomplete: 6,
  contactMade: 8,
  indicativeOffer: 9,
  inUnderwriting: 11,
  cancelled: 12,
  underwrittenOffer: 14,
  clientAccepted: 15,
  clientDecline: 16,
  nonResponsive: 17,
  lost: 18,
  awaitingClientDecision: 19,
  onHold: 21,
  lenderRejected: 22,
  awaitingClientContact: 23,
  professionsInstructed: 24,
  professionsSignOff: 25,
  finalCreditSignOff: 26,
});

export const FundingMatchStatusColor = Object.freeze({
  [FundingMatchStatus.submitted]: 'neutral',
  [FundingMatchStatus.incomplete]: 'neutral',
  [FundingMatchStatus.contactMade]: 'neutral',
  [FundingMatchStatus.inUnderwriting]: 'neutral',
  [FundingMatchStatus.nonResponsive]: 'neutral',
  [FundingMatchStatus.onHold]: 'neutral',
  [FundingMatchStatus.awaitingClientContact]: 'neutral',
  [FundingMatchStatus.professionsInstructed]: 'neutral',
  [FundingMatchStatus.passed]: 'neutral',

  [FundingMatchStatus.inReview]: 'green',
  [FundingMatchStatus.paidOut]: 'green',
  [FundingMatchStatus.indicativeOffer]: 'green',
  [FundingMatchStatus.underwrittenOffer]: 'green',
  [FundingMatchStatus.clientAccepted]: 'green',
  [FundingMatchStatus.professionsSignOff]: 'green',
  [FundingMatchStatus.finalCreditSignOff]: 'green',

  [FundingMatchStatus.awaitingClientDecision]: 'warning',

  [FundingMatchStatus.lenderDecline]: 'danger',
  [FundingMatchStatus.notAppropriate]: 'danger',
  [FundingMatchStatus.cancelled]: 'danger',
  [FundingMatchStatus.clientDecline]: 'danger',
  [FundingMatchStatus.lost]: 'danger',
  [FundingMatchStatus.lenderRejected]: 'danger',
});

export const FundingMatchStatusLabel = Object.freeze({
  [FundingMatchStatus.submitted]: 'Submitted',
  [FundingMatchStatus.inReview]: 'In Review',
  [FundingMatchStatus.passed]: 'Passed',
  [FundingMatchStatus.paidOut]: 'Paid Out',
  [FundingMatchStatus.lenderDecline]: 'Lender Declined',
  [FundingMatchStatus.notAppropriate]: 'N/A',
  [FundingMatchStatus.incomplete]: 'Incomplete',
  [FundingMatchStatus.contactMade]: 'Contact Made',
  [FundingMatchStatus.indicativeOffer]: 'Indicative Offer',
  [FundingMatchStatus.inUnderwriting]: 'In Underwriting',
  [FundingMatchStatus.cancelled]: 'Cancelled',
  [FundingMatchStatus.underwrittenOffer]: 'Underwritten Offer',
  [FundingMatchStatus.clientAccepted]: 'Client Preferred',
  [FundingMatchStatus.clientDecline]: 'Client Declined',
  [FundingMatchStatus.nonResponsive]: 'Closed, no contact',
  [FundingMatchStatus.lost]: 'Lost',
  [FundingMatchStatus.awaitingClientDecision]: 'Awaiting Client Decision',
  [FundingMatchStatus.onHold]: 'On Hold',
  [FundingMatchStatus.lenderRejected]: 'Failed',
  [FundingMatchStatus.awaitingClientContact]: 'Awaiting Client Contact',
  [FundingMatchStatus.professionsInstructed]: 'Professions Instructed',
  [FundingMatchStatus.professionsSignOff]: 'Professions Sign Off',
  [FundingMatchStatus.finalCreditSignOff]: 'Final Credit Sign Off',
});

export const CreditRiskFactors = Object.freeze({
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
});

export const CreditRiskFactorsLabel = Object.freeze({
  [CreditRiskFactors.positive]: 'Looking good',
  [CreditRiskFactors.negative]: 'Needs attention',
  [CreditRiskFactors.neutral]: 'Low impact',
});

export const LoadingSteps = Object.freeze({
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
});

const TEN_SECONDS = 10 * 1000;
const THIRTY_SECONDS = 30 * 1000;
const FORTY_FIVE_SECONDS = 45 * 1000;
const SIXTY_SECONDS = 60 * 1000;

export const CreditProfileLoadingStepTimes = Object.freeze({
  [LoadingSteps[0]]: 0,
  [LoadingSteps[1]]: TEN_SECONDS + 1,
  [LoadingSteps[2]]: THIRTY_SECONDS + 1,
  [LoadingSteps[3]]: FORTY_FIVE_SECONDS + 1,
  [LoadingSteps[4]]: SIXTY_SECONDS + 1,
});

export const CreditProfileLoadingStepLabels = Object.freeze({
  [LoadingSteps[0]]: 'Generating your credit profile.',
  [LoadingSteps[1]]: 'Loading your credit profile.',
  [LoadingSteps[2]]: 'Still loading your credit profile.',
  [LoadingSteps[3]]: 'We’re experiencing a delay loading your profile.',
  [LoadingSteps[4]]:
    'Due to high volume, your credit profile is waiting in a queue. Please refresh or return in a couple of minutes to view it.',
});

export const CashAndCapitalProductName = Object.freeze({
  assetFinance: 'ASSET_FINANCE',
  bank: 'BANK',
  card: 'CREDIT_CARD',
  mortgage: 'MORTGAGE',
  telecom: 'TELECOM',
  utility: 'UTILITY',
  currentAccounts: "CURRENT_ACCOUNTS",
  other: 'OTHER',
});

export const CashAndCapitalProductNameLabel = Object.freeze({
  [CashAndCapitalProductName.assetFinance]: 'Asset Finance',
  [CashAndCapitalProductName.bank]: 'Banks',
  [CashAndCapitalProductName.card]: 'Cards',
  [CashAndCapitalProductName.mortgage]: 'Mortgages',
  [CashAndCapitalProductName.telecom]: 'Telecoms',
  [CashAndCapitalProductName.utility]: 'Utilities',
  [CashAndCapitalProductName.currentAccounts]: 'Current accounts',
  [CashAndCapitalProductName.other]: 'Other',
});

export const CashAndCapitalProductNameIcon = Object.freeze({
  [CashAndCapitalProductName.assetFinance]: IconCar,
  [CashAndCapitalProductName.bank]: IconBank,
  [CashAndCapitalProductName.card]: IconCard,
  [CashAndCapitalProductName.mortgage]: IconHouseWork,
  [CashAndCapitalProductName.telecom]: IconMobilePhone,
  [CashAndCapitalProductName.utility]: IconLightbulb,
  [CashAndCapitalProductName.currentAccounts]: IconBank,
  [CashAndCapitalProductName.other]: IconFile,
});

export const CashAndCapitalStatus = Object.freeze({
  positive: 'LOOKING_GOOD',
  negative: 'NEEDS_ATTENTION',
  neutral: 'NEUTRAL',
});

export const CashAndCapitalStatusColor = Object.freeze({
  [CashAndCapitalStatus.positive]: 'green',
  [CashAndCapitalStatus.negative]: 'warning',
  [CashAndCapitalStatus.neutral]: 'grey',
});

export const CashAndCapitalStatusTextColor = Object.freeze({
  [CashAndCapitalStatus.positive]: 'green-500',
  [CashAndCapitalStatus.negative]: 'warning-500',
  [CashAndCapitalStatus.neutral]: 'grey-900',
});

export const CashAndCapitalTabs = Object.freeze({
  bank: 'BANK_ACCOUNT',
  credit: 'CREDIT_LINE',
});

export const CashAndCapitalTabsLabel = Object.freeze({
  [CashAndCapitalTabs.bank]: 'Bank accounts',
  [CashAndCapitalTabs.credit]: 'Credit accounts',
});

export const CashAndCapitalBankTurnoverFlags = Object.freeze({
  gross: 'G',
  net: 'N',
});

export const CashAndCapitalFrequencyLabel = Object.freeze({
  W: 'weekly',
  F: 'fortnightly',
  M: 'monthly',
  Q: 'quarterly',
  A: 'annually',
  P: 'periodically',
});

export const CashAndCapitalBankTurnoverFlagsLabel = Object.freeze({
  [CashAndCapitalBankTurnoverFlags.gross]: 'gross',
  [CashAndCapitalBankTurnoverFlags.net]: 'net',
});

export const CashAndCapitalBankTurnoverFlagsInclusionLabel = Object.freeze({
  [CashAndCapitalBankTurnoverFlags.gross]: 'including',
  [CashAndCapitalBankTurnoverFlags.net]: 'excluding',
});

export const DirectorsFormLoadingStepTimes = Object.freeze({
  [LoadingSteps[0]]: 0,
  [LoadingSteps[1]]: TEN_SECONDS + 1,
  [LoadingSteps[2]]: THIRTY_SECONDS + 1,
  [LoadingSteps[3]]: FORTY_FIVE_SECONDS + 1,
  [LoadingSteps[4]]: SIXTY_SECONDS + 1,
});

export const DirectorsFormLoadingStepLabels = Object.freeze({
  [LoadingSteps[0]]: 'Retrieving company directors.',
  [LoadingSteps[1]]: 'Loading your company directors.',
  [LoadingSteps[2]]: 'Still loading your company directors.',
  [LoadingSteps[3]]: 'We’re experiencing a delay loading your directors.',
  [LoadingSteps[4]]:
    'Due to high volume, loading your directors is waiting in a queue. Please refresh or return in a couple of minutes to view it.',
});
